<template>
  <div class="ll-wizard">
    <!-- Header -->
    <header class="header">
      <div class="brand">
        <div class="brand-text">
          <h1 class="page-heading">LOTLEDGER</h1>
          <div class="subtitle">Quick Add • Tell Me What You Sold</div>
        </div>
      </div>

      <div class="header-actions">
        <button class="pill ghost" type="button" @click="$emit('exit')">Exit</button>
      </div>
    </header>

    <main class="menu">
      <!-- Wizard Card -->
      <section class="row open" ref="heroCard" @click="focusComposer(true)">
        <div class="row-header" style="cursor: default;">
          <span class="big-letter">Q</span>
          <span class="word">Question</span>

          <button
            class="pill small ghost"
            type="button"
            @click.stop="detailsOpen = !detailsOpen"
          >
            {{ detailsOpen ? "Hide Details" : "Show Details" }}
          </button>
        </div>

        <div class="row-body">
          <div class="prompt">{{ currentPrompt }}</div>

          <div v-if="quickReplies.length" class="chips">
            <button
              v-for="qr in quickReplies"
              :key="qr"
              class="chip"
              type="button"
              :disabled="loading"
              @click.stop="send(qr)"
            >
              {{ qr }}
            </button>
          </div>

          <form class="composer" @submit.prevent="send(input)">
            <input
              ref="composerInput"
              v-model="input"
              class="composer-input"
              :type="composerType"
              :inputmode="composerType === 'date' ? undefined : 'text'"
              enterkeyhint="send"
              autocomplete="off"
              autocorrect="on"
              autocapitalize="sentences"
              spellcheck="true"
              :placeholder="composerPlaceholder"
              :disabled="loading"
              @focus="hasUserFocused = true"
            />
            <button class="pill send" type="submit" :disabled="loading || !input.trim()">
              Send
            </button>
          </form>

          <!-- Optional voice input if you want it here too -->
          <VoiceInput
            :disabled="loading"
            :autoStopOnFinal="true"
            :showTranscript="true"
            @final="onVoiceFinal"
          />

          <div v-if="loading" class="loading">
            <div class="spinner" aria-hidden="true"></div>
            <div class="loading-text">Thinking…</div>
          </div>

          <div v-if="hasDraft" class="draft-row">
            <div class="draft-pill">
              Draft in progress: <strong>{{ draftLineCount }}</strong> line(s)
            </div>
            <button class="pill" type="button" :disabled="loading" @click="reviewDraft">
              Review Draft
            </button>
          </div>
        </div>
      </section>

      <!-- Details -->
      <section v-if="detailsOpen" class="row open">
        <button class="row-header" type="button" @click="detailsOpen = false">
          <span class="big-letter" style="background:#327ba8;">D</span>
          <span class="word">Details</span>
          <span class="chev" aria-hidden="true">▾</span>
        </button>

        <div class="row-body">
          <div class="chat-box" ref="chatBox">
            <div v-for="m in messages" :key="m.id" class="msg-row" :class="m.role">
              <div class="msg-bubble">{{ m.text }}</div>
            </div>

            <div v-if="loading" class="msg-row assistant">
              <div class="msg-bubble typing">…</div>
            </div>
          </div>
        </div>
      </section>
    </main>

    <footer class="footer">
      <div class="foot-left">© {{ new Date().getFullYear() }} LotLedger</div>
      <div class="foot-right">
        <span class="foot-link">Sales Wizard</span>
      </div>
    </footer>
  </div>
</template>

<script>
import api from "@/services/api";
import VoiceInput from "@/components/voice/VoiceInput.vue";

export default {
  name: "SalesAssistantMode",
  components: { VoiceInput },
  emits: ["exit", "review"],

  data() {
    return {
      loading: false,
      input: "",
      messages: [],
      quickReplies: [],
      draft: {},

      supportsDate: true,
      detailsOpen: false,
      hasUserFocused: false,

      
      
    };
  },

  computed: {
    hasDraft() {
      return this.draft && typeof this.draft === "object" && Object.keys(this.draft).length > 0;
    },
    draftLineCount() {
      return Array.isArray(this.draft?.items) ? this.draft.items.length : 0;
    },
    currentPrompt() {
      const lastAssistant = [...this.messages].reverse().find((m) => m.role === "assistant");
      let t =
        lastAssistant?.text ||
        "What did you sell? Include qty and unit price if you know it.";

      // Rewrite ISO hint from backend to your preferred format
      t = t.replace(/\(YYYY-MM-DD\)/g, "(MM/DD/YYYY)");
      return t;
    },
    isAskingForDate() {
      const p = this.currentPrompt || "";
      return /what date/i.test(p) || /\bdate\b/i.test(p);
    },
    composerType() {
      return this.isAskingForDate && this.supportsDate ? "date" : "text";
    },
    composerPlaceholder() {
      return this.isAskingForDate ? "MM/DD/YYYY" : "Type your answer…";
    },
  },

  methods: {
    push(role, text) {
      this.messages.push({ id: crypto.randomUUID(), role, text });
    },

    focusComposer(force = false) {
      this.$nextTick(() => {
        const el = this.$refs.composerInput;
        if (!el) return;

        if (force || this.hasUserFocused) el.focus({ preventScroll: true });

        const hero = this.$refs.heroCard;
        if (hero?.scrollIntoView) hero.scrollIntoView({ block: "start", behavior: "smooth" });
      });
    },

    detectDateSupport() {
      const i = document.createElement("input");
      i.setAttribute("type", "date");
      i.value = "2026-01-21";
      return i.type === "date" && i.value === "2026-01-21";
    },

    scrollChatToBottom() {
      this.$nextTick(() => {
        const el = this.$refs.chatBox;
        if (el) el.scrollTop = el.scrollHeight;
      });
    },

    async callBackend(userMessage, { silentUser = false } = {}) {
      this.loading = true;
      try {
        const payload = {
          companion: "add-sale",      // ✅ IMPORTANT: sales wizard          
          user_message: userMessage,
          draft: this.draft,
        };

        const res = await api.post("/companions/message", payload);
        const data = res.data;

        if (!data?.ok) {
          this.push("assistant", data?.message || "Something went wrong.");
          return;
        }

        if (!silentUser) this.push("user", userMessage);
        if (data?.assistant_message) this.push("assistant", data.assistant_message);

        this.quickReplies = Array.isArray(data?.quick_replies) ? data.quick_replies : [];

        if (data?.draft && typeof data.draft === "object") {
          this.draft = data.draft;
        }

        if (this.detailsOpen) this.scrollChatToBottom();
        return data;
      } catch (err) {
        console.error("[SalesAssistantMode] call failed:", err);
        this.push("assistant", "Companion had trouble responding.");
      } finally {
        this.loading = false;
        this.focusComposer(false);
      }
    },

    onVoiceFinal(text) {
      const t = String(text || "").trim();
      if (!t) return;

      this.input = t;

      const qr = this.quickReplies.find(q => q.toLowerCase() === t.toLowerCase());
      if (qr) {
        this.send(qr);
        return;
      }

      if (!this.quickReplies.length) {
        this.send(t);
        return;
      }

      this.focusComposer(true);
    },

    async send(text) {
      const t = String(text || "").trim();
      if (!t) return;

      this.input = "";
      this.focusComposer(true);

      await this.callBackend(t);
    },

    async reviewDraft() {
      await this.callBackend("Ready to review.", { silentUser: true });
      this.$emit("review", this.draft);
    },
  },

  async mounted() {
    this.supportsDate = this.detectDateSupport();
    await this.callBackend("", { silentUser: true });
    this.focusComposer(false);
  },
};
</script>

<style scoped>
/* Same styling as AssistantMode.vue (inventory) */
.ll-wizard {
  min-height: 100vh;
  font-family: "Poppins", sans-serif;
  background: linear-gradient(180deg, #9cd0f2 0%, #eef4fb 55%, #ffffff 100%);
  padding: 22px;
  color: #1f2a35;
}

.header {
  background: rgba(255, 255, 255, 0.75);
  border: 1px solid rgba(131, 167, 203, 0.8);
  border-radius: 18px;
  padding: 16px 18px;
  box-shadow: 0px 6px 18px rgba(0, 0, 0, 0.08);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.page-heading {
  margin: 0;
  font-size: 28px;
  letter-spacing: 2px;
  font-weight: 800;
}

.subtitle {
  margin-top: 2px;
  font-size: 13px;
  opacity: 0.75;
  font-weight: 600;
}

.header-actions {
  display: flex;
  gap: 10px;
  align-items: center;
}

.pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 10px 12px;
  border-radius: 999px;
  font-weight: 800;
  color: white;
  background: #327ba8;
  border: 1px solid rgba(50, 123, 168, 0.25);
  cursor: pointer;
}

.pill:hover { background: #286c98; }
.pill.ghost { background: #4f6773; }
.pill.ghost:hover { background: #3a4a5e; }
.pill.small { padding: 8px 10px; font-size: 12px; }
.pill.send { background: #288671; border-color: rgba(40, 134, 113, 0.25); }
.pill.send:hover { background: #237a4b; }

.menu { margin-top: 16px; display: grid; gap: 12px; }

.row {
  border-radius: 16px;
  overflow: hidden;
  border: 1px solid rgba(131, 167, 203, 0.85);
  background: rgba(255, 255, 255, 0.8);
  box-shadow: 0px 6px 16px rgba(0, 0, 0, 0.06);
}

.row-header {
  width: 100%;
  border: none;
  background: transparent;
  padding: 14px 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  text-align: left;
}

.big-letter {
  width: 40px;
  height: 40px;
  border-radius: 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: 900;
  font-size: 18px;
  color: white;
  background: #288671;
  box-shadow: 0px 6px 14px rgba(40, 134, 113, 0.25);
}

.word { font-size: 18px; font-weight: 900; letter-spacing: 0.5px; }
.chev { margin-left: auto; font-size: 18px; opacity: 0.75; font-weight: 900; }

.row-body { padding: 0 16px 16px 16px; }

.prompt {
  margin-top: 2px;
  font-size: 16px;
  font-weight: 800;
  line-height: 1.4rem;
  color: #1f2a35;
}

.chips { margin-top: 12px; display: flex; flex-wrap: wrap; gap: 8px; }
.chip {
  border: 1px solid rgba(176, 196, 222, 0.85);
  background: #f6fbff;
  border-radius: 999px;
  padding: 10px 12px;
  font-weight: 800;
  cursor: pointer;
}
.chip:disabled { opacity: 0.6; cursor: not-allowed; }

.composer { margin-top: 12px; display: grid; grid-template-columns: 1fr auto; gap: 10px; align-items: center; width: 100%; }
.composer-input {
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
  padding: 12px 12px;
  border-radius: 14px;
  border: 1px solid rgba(176, 196, 222, 0.95);
  background: #f6fbff;
  font-weight: 700;
  font-size: 16px;
  line-height: 20px;
}

.loading { margin-top: 12px; display: flex; gap: 10px; align-items: center; opacity: 0.75; font-weight: 800; }
.spinner { width: 16px; height: 16px; border-radius: 999px; border: 2px solid rgba(15, 23, 42, 0.2); border-top-color: rgba(15, 23, 42, 0.9); animation: spin 0.7s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }

.draft-row { margin-top: 14px; display: flex; justify-content: space-between; gap: 10px; align-items: center; flex-wrap: wrap; }
.draft-pill { border: 1px solid rgba(176, 196, 222, 0.85); background: #f6fbff; border-radius: 999px; padding: 10px 12px; font-weight: 800; color: #1f2a35; }

.chat-box {
  margin-top: 10px;
  background: #f6fbff;
  border: 1px solid rgba(176, 196, 222, 0.85);
  border-radius: 14px;
  padding: 12px;
  height: 34vh;
  overflow: auto;
}

.msg-row { display: flex; margin-bottom: 10px; }
.msg-row.user { justify-content: flex-end; }
.msg-row.assistant { justify-content: flex-start; }

.msg-bubble {
  max-width: 88%;
  padding: 10px 12px;
  border-radius: 14px;
  border: 1px solid rgba(176, 196, 222, 0.95);
  background: white;
  font-weight: 700;
  line-height: 1.25rem;
}

.msg-row.user .msg-bubble { background: #327ba8; border-color: #327ba8; color: white; }
.typing { opacity: 0.7; }

.footer { margin-top: 18px; padding: 10px 4px; display: flex; justify-content: space-between; opacity: 0.75; font-weight: 700; }

@media (max-width: 980px) {
  .header { flex-direction: column; align-items: flex-start; }
  .header-actions { width: 100%; }
  .pill { width: 100%; }
  .composer { grid-template-columns: 1fr; }
  .pill.send { width: 100%; }
}

* { box-sizing: border-box; }
</style>
